import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { AppConstants } from "../Appconstants";
import { flattenProductions } from "../Dashboard/utility/columnWidth";

export const downloadExcel = async (data: any, name: string, error?: any) => {
  if (
    name === "BulkUploadAlternateUnit" ||
    name === "Bulk Upload Alternate Unit Error Data."
  ) {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Sheet1");
    const hiddenSheet = workbook.addWorksheet("DataValidation", {
      state: "veryHidden",
    });

    const units = data.map((unit: any) => `${unit.name} (${unit.code})`);

    // Add units to hidden sheet in column A
    units.forEach((unit: any, index: number) => {
      hiddenSheet.getCell(`A${index + 1}`).value = unit;
    });

    const columns = [
      { header: "* Item ID", key: "* Item ID" },
      { header: "Item Name", key: "Item Name" },
      { header: "* Base Unit", key: "* Base Unit" },
      { header: "* Alternate Unit", key: "* Alternate Unit" },
      { header: "* Conversion Factor", key: "* Conversion Factor" },
      { header: "Unit Conversion Formula", key: "Unit Conversion Formula" },
    ];

    if (error) {
      columns.push({ header: "Error", key: "Error" });
    }

    worksheet.columns = columns;

    const sampleData = error
      ? error
      : [
        {
          "* Item ID": 1,
          "Item Name": "Laptop",
          "* Base Unit": units[0],
          "* Alternate Unit": units[1] || "",
          "* Conversion Factor": 10,
        },
      ];

    // Add sample + remaining blank rows up to 1000
    for (let i = 0; i < 1000; i++) {
      const rowData = sampleData[i] || {
        "* Item ID": "",
        "Item Name": "",
        "* Base Unit": "",
        "* Alternate Unit": "",
        "* Conversion Factor": "",
      };

      const rowNumber = i + 2;
      worksheet.addRow({
        ...rowData,
        "Unit Conversion Formula": {
          formula: `=IF(AND(ISNUMBER(E${rowNumber}), C${rowNumber}<>"", D${rowNumber}<>""), "1 " & D${rowNumber} & " = " & E${rowNumber} & " * " & C${rowNumber}, "")`,
        },
      });

      const listRange = `DataValidation!$A$1:$A$${units.length}`;

      worksheet.getCell(`C${rowNumber}`).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: [listRange],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Base Unit.",
      };

      worksheet.getCell(`D${rowNumber}`).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: [listRange],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Alternate Unit.",
      };

      worksheet.getCell(`E${rowNumber}`).dataValidation = {
        type: "decimal",
        operator: "greaterThan",
        formulae: [0],
        allowBlank: false,
        showErrorMessage: true,
        errorTitle: "Invalid Number",
        error: "Conversion factor must be a number greater than 0.",
      };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${name}.xlsx`);
    return;
  }

  // Fallback for other file names (uses XLSX lib)
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  XLSX.writeFile(workbook, name + ".xlsx");
};

export const downloadExcelWithDropDown = async (
  data: any[],
  name: string,
  categories: any,
  error: null | boolean,
  customFields: any = [],
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  // Extract sub-categories and micro-categories
  let subCategories: any[] = [];
  let microCategories: any[] = [];

  categories.forEach((category: any) => {
    if (category?.child?.length) subCategories.push(...category.child);
  });

  subCategories.forEach((subCategory: any) => {
    if (subCategory?.child?.length) microCategories.push(...subCategory.child);
  });

  // Define base columns
  const baseColumns = [
    { header: "Item ID", key: "Item ID" },
    { header: "Item Name", key: "Item Name" },
    { header: "Item type", key: "Item type" },
    { header: "Category", key: "Category" },
    { header: "Sub Category", key: "Sub Category" },
    { header: "Micro Category", key: "Micro Category" },
    { header: "HSN", key: "HSN" },
    { header: "Price", key: "Price" },
    { header: "MRP", key: "MRP" },
    { header: "Tax Type", key: "Tax Type" },
    { header: "Tax", key: "Tax" },
    { header: "Min Stock", key: "Min Stock" },
    { header: "Max Stock", key: "Max Stock" },
    { header: "Description", key: "Description" },
  ];

  // Handle custom fields
  const customFieldColumns: { header: string; key: string }[] = [];
  const customSelectDropdowns: { [key: string]: string[] } = {};
  const customNumberFields: string[] = [];

  if (Array.isArray(customFields)) {
    customFields
      ?.filter((data: any) => data.documentType == "item")
      .forEach((field: any) => {
        const header = field?.fieldName;
        const key = field?.fieldName;
        customFieldColumns.push({ header, key });

        if (field?.type === "select") {
          customSelectDropdowns[header] = JSON.parse(field.options || "[]");
        }

        if (field?.type === "number") {
          customNumberFields.push(header);
        }
      });
  }

  let columns = [...baseColumns, ...customFieldColumns];

  if (error) {
    columns.push({ header: "Error", key: "Error" });
  }

  worksheet.columns = columns;

  // Add data rows
  data.forEach((item) => worksheet.addRow({ ...item }));

  // Unlock all cells
  // worksheet.eachRow((row, rowNumber) => {
  //   row.eachCell({ includeEmpty: true }, (cell) => {
  //     cell.protection = { locked: false };
  //   });
  // });

  // worksheet.columns.forEach((col) => {
  //   col.eachCell?.({ includeEmpty: true }, (cell) => {
  //     cell.protection = { locked: false };
  //   });
  // });

  // Lock only "Item ID" column
  // worksheet.getColumn("Item ID").eachCell((cell, rowNumber) => {
  //   if (rowNumber > 1) cell.protection = { locked: true };
  // });

  // Utility function for applying list validations
  const applyListValidation = (columnKey: string, options: string[]) => {
    worksheet.getColumn(columnKey).eachCell((cell, rowNumber) => {
      if (rowNumber > 1) {
        cell.dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`"${options.join(",")}"`],
          showErrorMessage: true,
          errorTitle: "Invalid Selection",
          error: `Please select a valid ${columnKey}.`,
        };
      }
    });
  };

  // Set Category, Sub Category, Micro Category dropdowns
  const handleCategoryDropdown = (columnKey: string, sourceList: any[]) => {
    worksheet.getColumn(columnKey).eachCell((cell, rowNumber) => {
      if (rowNumber > 1) {
        const id = data[rowNumber - 2]?.[columnKey];
        const match = sourceList.find((item) => item.id === id);
        cell.value = match ? match.name : error ? id || "" : "";
        cell.dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`"${sourceList.map((i) => i.name).join(",")}"`],
          showErrorMessage: true,
          errorTitle: "Invalid Selection",
          error: `Please select a valid ${columnKey}.`,
        };
      }
    });
  };

  handleCategoryDropdown("Category", categories);
  handleCategoryDropdown("Sub Category", subCategories);
  handleCategoryDropdown("Micro Category", microCategories);

  // Apply predefined dropdowns
  applyListValidation("Tax", [
    "0",
    "0.1",
    "0.25",
    "3",
    "5",
    "6",
    "12",
    "18",
    "28",
  ]);
  applyListValidation("Tax Type", ["Inclusive", "Exclusive"]);
  applyListValidation("Item type", ["Buy", "Sell", "Both"]);

  // Numeric validations
  const numericColumns = [
    "Price",
    "MRP",
    "Min Stock",
    "Max Stock",
    "Current Stock",
    ...customNumberFields,
  ];
  // ✅ SAFELY apply custom select dropdowns using column header mapping
  const headerRow = worksheet.getRow(1);
  const headerMap: { [header: string]: number } = {};
  headerRow.eachCell((cell, colNumber) => {
    headerMap[cell.value as string] = colNumber;
  });

  numericColumns.forEach((columnName) => {
    const columnIndex = headerMap[columnName];
    if (columnIndex) {
      worksheet.getColumn(columnIndex).eachCell((cell, rowNumber) => {
        if (rowNumber > 1) {
          cell.dataValidation = {
            type: "decimal",
            operator: "greaterThanOrEqual",
            formulae: [0],
            allowBlank: true,
            showErrorMessage: true,
            errorTitle: "Invalid Input",
            error: `Please enter a valid number for ${columnName}.`,
          };
        }
      });
    }
  });

  Object.entries(customSelectDropdowns).forEach(([key, options]) => {
    const colIndex = headerMap[key];
    if (!colIndex) return;

    worksheet.getColumn(colIndex).eachCell((cell, rowNumber) => {
      if (rowNumber > 1) {
        cell.dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`"${options.join(",")}"`],
          showErrorMessage: true,
          errorTitle: "Invalid Selection",
          error: `Please select a valid value for ${key}.`,
        };
      }
    });
  });

  // Protect sheet
  // await worksheet.protect("password", {
  //   selectLockedCells: true,
  //   selectUnlockedCells: true,
  // });

  // Export
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    `${name}.xlsx`,
  );
};

function getExcelColumnLetter(colIndex: number): string {
  let letter = "";
  while (colIndex > 0) {
    const remainder = (colIndex - 1) % 26;
    letter = String.fromCharCode(65 + remainder) + letter;
    colIndex = Math.floor((colIndex - 1) / 26);
  }
  return letter;
}

export const downloadExcelTemplateForBulkUploadBOM = async (
  name: string,
  categories: any,
  metricsUnit: any,
  storeData: any,
  bomSeriesData: any,
  productionProcessData: any,
  errorData?: any,
) => {
  const workbook = new ExcelJS.Workbook();

  const worksheetFG = workbook.addWorksheet("FG");
  const worksheetRM = workbook.addWorksheet("RM");
  const worksheetLeftover = workbook.addWorksheet("Leftover");
  const worksheetProcess = workbook.addWorksheet("Production Process");
  const worksheetOtherCharges = workbook.addWorksheet("Other Charges");

  const storeList: string[] = [];

  for (const element of storeData ?? []) {
    if (!storeList.includes(element?.name)) {
      storeList.push(element?.name);
    }
  }
  if (!storeList?.length) storeList.push("No Store Available");

  const productionProcessList: string[] = [];

  for (const element of productionProcessData ?? []) {
    productionProcessList.push(element?.processName);
  }

  const FGBaseColumns = [
    { header: "Sl_No", key: "Sl_No" },
    {
      header: "FG Item ID",
      key: "FG Item ID",
    },
    {
      header: "FG Item Name",
      key: "FG Item Name",
    },
    {
      header: "FG Quantity",
      key: "FG Quantity",
    },
    {
      header: "FG UOM",
      key: "FG UOM",
    },
    {
      header: "BOM Series",
      key: "BOM Series",
    },
    {
      header: "BOM Name",
      key: "BOM Name",
    },
    {
      header: "FG Store",
      key: "FG Store",
    },
    {
      header: "BOM Description",
      key: "BOM Description",
    },
    ...(errorData ? [{ header: "Error", key: "Error" }] : []),
  ];

  const RMBaseColumns = [
    {
      header: "Sl_No (FG Sheet)",
      key: "Sl_No (FG Sheet)",
    },
    {
      header: "FG Item ID",
      key: "FG Item ID",
    },
    {
      header: "BOM Series",
      key: "BOM Series",
    },
    {
      header: "RM Sl_No",
      key: "RM Sl_No",
    },
    {
      header: "Item Id",
      key: "Item Id",
    },
    {
      header: "Item Name",
      key: "Item Name",
    },
    {
      header: "Quantity",
      key: "Quantity",
    },
    {
      header: "UOM",
      key: "UOM",
    },
    {
      header: "RM Store",
      key: "RM Store",
    },
    ...(errorData ? [{ header: "Error", key: "Error" }] : []),
  ];

  const LeftoverBaseColumns = [
    {
      header: "Sl_No (FG Sheet)",
      key: "Sl_No (FG Sheet)",
    },
    {
      header: "FG Item ID",
      key: "FG Item ID",
    },
    {
      header: "BOM Series",
      key: "BOM Series",
    },
    {
      header: "Leftover Sl_No",
      key: "Leftover Sl_No",
    },
    {
      header: "Item Id",
      key: "Item Id",
    },
    {
      header: "Item Name",
      key: "Item Name",
    },
    {
      header: "Quantity",
      key: "Quantity",
    },
    {
      header: "UOM",
      key: "UOM",
    },
    {
      header: "Leftover Store",
      key: "Leftover Store",
    },
    ...(errorData ? [{ header: "Error", key: "Error" }] : []),
  ];

  const processBaseColumns = [
    {
      header: "Sl_No (FG Sheet)",
      key: "Sl_No (FG Sheet)",
    },
    {
      header: "FG Item Id",
      key: "FG Item Id",
    },
    {
      header: "BOM Series",
      key: "BOM Series",
    },
    {
      header: "Process Sl_No",
      key: "Process Sl_No",
    },
    {
      header: "Process Id",
      key: "Process Id",
    },
    {
      header: "Process Name",
      key: "Process Name",
    },
    ...(errorData ? [{ header: "Error", key: "Error" }] : []),
  ];

  const otherChargesBaseColumns = [
    {
      header: "Sl_No (FG Sheet)",
      key: "Sl_No (FG Sheet)",
    },
    {
      header: "FG Item Id",
      key: "FG Item Id",
    },
    {
      header: "Charges Sl_No",
      key: "Charges Sl_No",
    },
    {
      header: "Charges Name",
      key: "Charges Name",
    },
    {
      header: "Amount (INR)",
      key: "Amount (INR)",
    },
    ...(errorData ? [{ header: "Error", key: "Error" }] : []),
  ];

  worksheetFG.columns = FGBaseColumns;
  worksheetRM.columns = RMBaseColumns;
  worksheetLeftover.columns = LeftoverBaseColumns;
  worksheetProcess.columns = processBaseColumns;
  worksheetOtherCharges.columns = otherChargesBaseColumns;

  const dummyRowFG: any = {
    Sl_No: 1,
    "FG Item ID": "FG001",
    "FG Item Name": "Sample Item",
    "FG Quantity": 100,
    "FG UOM": "",
    "BOM Series": "",
    "BOM Name": "Sample BOM",
    "FG Store": "",
    "BOM Description": "Sample description",
  };

  const dummyRowRM: any = {
    "Sl_No (FG Sheet)": 1,
    "FG Item ID": "FG001",
    "BOM Series": "",
    "RM Sl_No": 1,
    "Item Id": "RM001",
    "Item Name": "Sample RM Item",
    Quantity: 10,
    UOM: "",
    "RM Store": "",
  };

  const dummyRowLeftover: any = {
    "Sl_No (FG Sheet)": 1,
    "FG Item ID": "FG001",
    "BOM Series": "",
    "Leftover Sl_No": 1,
    "Item Id": "LF001",
    "Item Name": "Sample Leftover Item",
    Quantity: 5,
    UOM: "",
    "Leftover Store": "",
  };

  const dummyRowProcess: any = {
    "Sl_No (FG Sheet)": 1,
    "FG Item Id": "FG001",
    "BOM Series": "",
    "Process Sl_No": 1,
    "Process Id": "P001",
    "Process Name": "",
  };

  const dummyRowOtherCharges: any = {
    "Sl_No (FG Sheet)": 1,
    "FG Item Id": "FG001",
    "Charges Sl_No": 1,
    "Charges Name": "Sample Charge",
    "Amount (INR)": 100,
  };

  if (!errorData) {
    worksheetFG.addRow(dummyRowFG);
    worksheetRM.addRow(dummyRowRM);
    worksheetLeftover.addRow(dummyRowLeftover);
    worksheetProcess.addRow(dummyRowProcess);
    worksheetOtherCharges.addRow(dummyRowOtherCharges);
  }

  const addErrorRows = (
    worksheet: ExcelJS.Worksheet,
    baseColumns: any[],
    rows: any[],
  ) => {
    if (!rows?.length) return;

    rows.forEach((rowData) => {
      const formattedRow: any = {};

      baseColumns.forEach((col) => {
        formattedRow[col.key] = rowData?.[col.key] ?? "";
      });

      worksheet.addRow(formattedRow);
    });
  };

  if (errorData) {
    addErrorRows(worksheetFG, FGBaseColumns, errorData?.FG || []);
    addErrorRows(worksheetRM, RMBaseColumns, errorData?.RM || []);
    addErrorRows(
      worksheetLeftover,
      LeftoverBaseColumns,
      errorData?.Leftover || [],
    );
    addErrorRows(
      worksheetProcess,
      processBaseColumns,
      errorData?.["Production Process"] || [],
    );
    addErrorRows(
      worksheetOtherCharges,
      otherChargesBaseColumns,
      errorData?.["Other Charges"] || [],
    );
  }

  const dropdownSheet = workbook.addWorksheet("Dropdowns");
  dropdownSheet.state = "veryHidden";

  const addDropdownColumn = (
    values: string[],
    colIndex: number,
  ): string | null => {
    if (!values || !values.length) return null;
    values.forEach((val, i) => {
      dropdownSheet.getCell(i + 1, colIndex).value = val;
    });
    const colLetter = getExcelColumnLetter(colIndex);
    return `Dropdowns!$${colLetter}$1:$${colLetter}$${values.length}`;
  };

  const metricsUnitRange = addDropdownColumn(
    metricsUnit?.map((u: any) => u.code),
    1,
  );

  const storeRange = addDropdownColumn(storeList, 2);

  const bomSeries = addDropdownColumn(
    bomSeriesData?.map((b: any) => b.prefix) || [],
    3,
  );

  const productionProcesses = addDropdownColumn(productionProcessList, 4);

  const applyListValidation = (
    worksheet: ExcelJS.Worksheet,
    colKey: string,
    formula: string | null,
  ) => {
    if (!formula) return;
    const colIndex = worksheet.columns.findIndex((col) => col.key === colKey);
    if (colIndex === -1) return;

    for (let row = 2; row <= 1001; row++) {
      worksheet.getCell(row, colIndex + 1).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [formula],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: `Please select a valid value for ${colKey}.`,
      };
    }
  };

  // For FG Sheet
  applyListValidation(worksheetFG, "FG UOM", metricsUnitRange);
  applyListValidation(worksheetFG, "FG Store", storeRange);
  applyListValidation(worksheetFG, "BOM Series", bomSeries);

  // For RM Sheet
  applyListValidation(worksheetRM, "UOM", metricsUnitRange);
  applyListValidation(worksheetRM, "RM Store", storeRange);
  applyListValidation(worksheetRM, "BOM Series", bomSeries);

  // For Leftover Sheet
  applyListValidation(worksheetLeftover, "UOM", metricsUnitRange);
  applyListValidation(worksheetLeftover, "Leftover Store", storeRange);
  applyListValidation(worksheetLeftover, "BOM Series", bomSeries);

  // For Production Process Sheet
  applyListValidation(worksheetProcess, "BOM Series", bomSeries);
  applyListValidation(worksheetProcess, "Process Name", productionProcesses);

  const applyNumericValidation = (
    worksheet: ExcelJS.Worksheet,
    numericFields: string[],
  ) => {
    numericFields.forEach((fieldKey) => {
      const colIndex = worksheet.columns.findIndex(
        (col) => col.key === fieldKey,
      );
      if (colIndex === -1) return;

      for (let row = 2; row <= 1001; row++) {
        worksheet.getCell(row, colIndex + 1).dataValidation = {
          type: "decimal",
          operator: "greaterThanOrEqual",
          formulae: [0],
          allowBlank: true,
          showErrorMessage: true,
          errorTitle: "Invalid Input",
          error: `Please enter a valid number for ${fieldKey}.`,
        };
      }
    });
  };

  const numericFieldsFG = ["Sl_No", "FG Quantity"];
  const numericFieldsRM = ["Sl_No (FG Sheet)", "RM Sl_No", "Quantity"];
  const numericFieldsLeftover = [
    "Sl_No (FG Sheet)",
    "Leftover Sl_No",
    "Quantity",
  ];
  const numericFieldsProcess = ["Sl_No (FG Sheet)", "Process Sl_No"];
  const numericFieldsOtherCharges = [
    "Sl_No (FG Sheet)",
    "Charges Sl_No",
    "Amount (INR)",
  ];

  applyNumericValidation(worksheetFG, numericFieldsFG);
  applyNumericValidation(worksheetRM, numericFieldsRM);
  applyNumericValidation(worksheetLeftover, numericFieldsLeftover);
  applyNumericValidation(worksheetProcess, numericFieldsProcess);
  applyNumericValidation(worksheetOtherCharges, numericFieldsOtherCharges);

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${name}.xlsx`,
  );
};

export const downloadExcelTemplateForBulkupload = async (
  name: string,
  categories: any,
  metricsUnit: any,
  customFields: any,
  storeData: any,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  let subCategories: any[] = [];
  let microCategories: any[] = [];

  categories.forEach((category: any) => {
    if (category?.child?.length) {
      subCategories.push(...category.child);
    }
  });

  subCategories.forEach((subCategory: any) => {
    if (subCategory?.child?.length) {
      microCategories.push(...subCategory.child);
    }
  });

  const storeList: string[] = [];
  for (const element of storeData) {
    if (element.storeType?.includes("1"))
      storeList.push(`${element.name} (Instock)`);
    if (element.storeType?.includes("2"))
      storeList.push(`${element.name} (Reject)`);
  }
  if (!storeList?.length) storeList.push("No Store Available");

  // Base columns
  const baseColumns = [
    { header: "* Item ID", key: "Item ID" },
    { header: "* Item Name", key: "Item Name" },
    { header: "* Item Type", key: "Item Type" },
    { header: "Category", key: "Category" },
    { header: "Sub Category", key: "Sub Category" },
    { header: "Micro Category", key: "Micro Category" },
    { header: "* Metrics Unit", key: "Metrics Unit" },
    { header: "HSN", key: "HSN" },
    { header: "Store", key: "Store" },
    { header: "Current Stock", key: "Current Stock" },
    { header: "Price", key: "Price" },
    { header: "MRP", key: "MRP" },
    { header: "Tax Type", key: "Tax Type" },
    { header: "Tax", key: "Tax" },
    { header: "Min Stock", key: "Min Stock" },
    { header: "Max Stock", key: "Max Stock" },
    { header: "Description", key: "Description" },
  ];

  const customFieldColumns: { header: string; key: string }[] = [];
  const customSelectDropdowns: { [key: string]: string[] } = {};
  const customNumberFields: string[] = [];

  if (Array.isArray(customFields)) {
    customFields
      ?.filter((data: any) => data.documentType === "item")
      .forEach((field: any) => {
        const header = field?.fieldName;
        const key = field?.fieldName;
        customFieldColumns.push({ header, key });

        if (field?.type === "select") {
          const opts = JSON.parse(field.options || "[]");
          if (opts.length) customSelectDropdowns[key] = opts;
        }

        if (field?.type === "number") {
          customNumberFields.push(key);
        }
      });
  }

  worksheet.columns = [...baseColumns, ...customFieldColumns];

  // Dummy row
  const dummyRow: any = {
    "Item ID": "1001",
    "Item Name": "Sample Item",
    "Item Type": "Buy",
    Category: "",
    "Sub Category": "",
    "Micro Category": "",
    "Metrics Unit": "",
    HSN: "123456",
    Store: "",
    "Current Stock": 0,
    Price: 0,
    MRP: 0,
    "Tax Type": "",
    Tax: 0,
    "Min Stock": 1,
    "Max Stock": 100,
    Description: "Sample description",
  };
  customFieldColumns.forEach((col) => (dummyRow[col.key] = ""));
  worksheet.addRow(dummyRow);

  // Hidden dropdown sheet
  const dropdownSheet = workbook.addWorksheet("Dropdowns");
  dropdownSheet.state = "veryHidden";

  const addDropdownColumn = (
    values: string[],
    colIndex: number,
  ): string | null => {
    if (!values || !values.length) return null;
    values.forEach((val, i) => {
      dropdownSheet.getCell(i + 1, colIndex).value = val;
    });
    const colLetter = getExcelColumnLetter(colIndex);
    return `Dropdowns!$${colLetter}$1:$${colLetter}$${values.length}`;
  };

  // Predefined dropdowns
  const categoryRange = addDropdownColumn(
    categories.map((c: any) => c.name),
    1,
  );
  const subCategoryRange = addDropdownColumn(
    subCategories.map((s: any) => s.name),
    2,
  );
  const microCategoryRange = addDropdownColumn(
    microCategories.map((m: any) => m.name),
    3,
  );
  const metricsUnitRange = addDropdownColumn(
    metricsUnit.map((u: any) => u.code),
    4,
  );
  const storeRange = addDropdownColumn(storeList, 5);

  const applyListValidation = (colKey: string, formula: string | null) => {
    if (!formula) return;
    const colIndex = worksheet.columns.findIndex((col) => col.key === colKey);
    if (colIndex === -1) return;

    for (let row = 2; row <= 1001; row++) {
      worksheet.getCell(row, colIndex + 1).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [formula],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: `Please select a valid value for ${colKey}.`,
      };
    }
  };

  applyListValidation("Item Type", '"Buy,Sell,Both"');
  applyListValidation("Tax Type", '"Inclusive,Exclusive"');
  applyListValidation("Tax", '"0,0.1,0.25,3,5,6,12,18,28"');
  applyListValidation("Category", categoryRange);
  applyListValidation("Sub Category", subCategoryRange);
  applyListValidation("Micro Category", microCategoryRange);
  applyListValidation("Metrics Unit", metricsUnitRange);
  applyListValidation("Store", storeRange);

  // Numeric validations
  const numericFields = [
    "Price",
    "MRP",
    "Min Stock",
    "Max Stock",
    "Current Stock",
    ...customNumberFields,
  ];
  numericFields.forEach((fieldKey) => {
    const colIndex = worksheet.columns.findIndex((col) => col.key === fieldKey);
    if (colIndex === -1) return;

    for (let row = 2; row <= 1001; row++) {
      worksheet.getCell(row, colIndex + 1).dataValidation = {
        type: "decimal",
        operator: "greaterThanOrEqual",
        formulae: [0],
        allowBlank: true,
        showErrorMessage: true,
        errorTitle: "Invalid Input",
        error: `Please enter a valid number for ${fieldKey}.`,
      };
    }
  });

  // Custom select dropdowns
  let customDropdownStart = 10; // start column far away from predefined
  Object.entries(customSelectDropdowns).forEach(([key, options]) => {
    const colIndex = worksheet.columns.findIndex((col) => col.key === key);
    if (colIndex === -1) return;

    const range = addDropdownColumn(options, customDropdownStart++);
    if (!range) return;

    for (let row = 2; row <= 1001; row++) {
      worksheet.getCell(row, colIndex + 1).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`=${range}`],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: `Please select a valid value for ${key}.`,
      };
    }
  });

  // Export
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `${name}.xlsx`,
  );
};

export const downloadBulkUploadErrorData = async (
  data: any[],
  name: string,
  categories: any,
  metricsUnit: any,
  storeData: any,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  let subCategories: any = [];
  let microCategories: any = [];
  categories.forEach((category: any) => {
    if (category?.child?.length) {
      subCategories = [...subCategories, ...category.child];
    }
  });

  subCategories.forEach((subCategory: any) => {
    if (subCategory?.child?.length) {
      microCategories = [...microCategories, ...subCategory.child];
    }
  });

  const dropdownSheet = workbook.addWorksheet("Dropdowns");
  dropdownSheet.state = "veryHidden";
  metricsUnit.forEach((unit: any, index: number) => {
    dropdownSheet.getCell(`A${index + 1}`).value = unit.code;
  });

  const metricsRange = `Dropdowns!$A$1:$A$${metricsUnit.length}`;
  const storeList: string[] = [];
  for (const element of storeData) {
    if (element.storeType?.includes("1"))
      storeList.push(`${element.name} (Instock)`);
    if (element.storeType?.includes("2"))
      storeList.push(`${element.name} (Reject)`);
  }
  if (!storeList?.length) {
    storeList.push("No Store Available");
  }

  worksheet.columns = [
    { header: "* Item ID", key: "* Item ID" },
    { header: "* Item Name", key: "* Item Name" },
    { header: "* Item Type", key: "* Item Type" },
    { header: "* Metrics Unit", key: "* Metrics Unit" },
    { header: "Category", key: "Category" },
    { header: "Sub Category", key: "Sub Category" },
    { header: "Micro Category", key: "Micro Category" },
    { header: "HSN", key: "HSN" },
    { header: "Store", key: "Store" }, // <-- NEW
    { header: "Current Stock", key: "Current Stock" },
    { header: "Price", key: "Price" },
    { header: "MRP", key: "MRP" },
    { header: "Tax Type", key: "Tax Type" },
    { header: "Tax", key: "Tax" },
    { header: "Min Stock", key: "Min Stock" },
    { header: "Max Stock", key: "Max Stock" },
    { header: "Description", key: "Description" },
    { header: "Error", key: "Error" },
  ];
  data.forEach((item) => {
    worksheet.addRow({ ...item });
  });

  worksheet.getColumn("Category").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      const index = categories?.findIndex(
        (category: any) => category?.id === data[rowNumber - 2]?.Category,
      );
      if (index != -1) {
        cell.value = categories[index]?.name;
      } else {
        cell.value = "";
      }
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [
          `"${categories.map((category: any) => category.name).join(",")}"`,
        ],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Category.",
      };
    }
  });

  worksheet.getColumn("Sub Category").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      const index = subCategories?.findIndex(
        (subcategory: any) =>
          subcategory?.id === data[rowNumber - 2]?.["Sub Category"],
      );
      if (index != -1) {
        cell.value = subCategories[index]?.name;
      } else {
        cell.value = "";
      }
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [
          `"${subCategories.map((subCategory: any) => subCategory.name).join(",")}"`,
        ],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Sub Category.",
      };
    }
  });

  worksheet.getColumn("Micro Category").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      const index = microCategories?.findIndex(
        (microCategory: any) =>
          microCategory?.id === data[rowNumber - 2]?.["Micro Category"],
      );
      if (index != -1) {
        cell.value = microCategories[index]?.name;
      } else {
        cell.value = "";
      }
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [
          `"${microCategories.map((microCategory: any) => microCategory.name).join(",")}"`,
        ],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Micro Category.",
      };
    }
  });
  worksheet.getColumn("* Metrics Unit").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`=${metricsRange}`],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Metrics Unit.",
      };
    }
  });
  const numericColumns = ["Price", "Tax", "Min Stock", "Max Stock"];
  numericColumns.forEach((column) => {
    worksheet.getColumn(column).eachCell((cell, rowNumber) => {
      if (rowNumber > 1) {
        cell.dataValidation = {
          type: "decimal",
          operator: "greaterThanOrEqual",
          formulae: [0],
          allowBlank: true,
          showErrorMessage: true,
          errorTitle: "Invalid Input",
          error: `Please enter a valid numeric value for ${column}.`,
        };
      }
    });
  });

  worksheet.getColumn("Tax Type").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"Inclusive,Exclusive"'],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Tax Type.",
      };
    }
  });

  worksheet.getColumn("Tax").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"0,0.1,0.25,3,5,6,12,18,28"'],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Tax value.",
      };
    }
  });

  worksheet.getColumn("* Item Type")?.eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"Buy,Sell,Both"'],
        showErrorMessage: true,
        errorTitle: "Invalid Selection",
        error: "Please select a valid Item type.",
      };
    }
  });

  const storeRange = `Dropdowns!$B$1:$B$${storeList.length}`;
  worksheet.getColumn("Store").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`=${storeRange}`],
        showErrorMessage: true,
        errorTitle: "Invalid Store",
        error: "Please select a valid Store.",
      };
    }
  });
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    `${name}.xlsx`,
  );
};

const generateMainReportData = (
  documentType: string,
  documents: any[],
  columns: any,
  columnPreference: any,
) => {
  if (documents.length === 0) {
    return [{}];
  }
  const filteredDocs = documents;
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const yyyy = today.getFullYear();
  const formattedDate = `${dd}:${mm}:${yyyy}`;
  const isDateColumn = (value: any) =>
    typeof value === "string" && /^\d{2}-\d{2}-\d{4}$/.test(value);

  return filteredDocs.map((doc) => {
    let obj: any = {};
    for (const element of columns) {
      if (
        columnPreference.length != 0 &&
        !columnPreference.includes(element.dataIndex)
      ) {
        continue;
      }

      if (
        documentType === "Daily Stock report" &&
        isDateColumn(element?.dataIndex)
      ) {
        const dateKey = element.dataIndex;
        const baseQty = Math.abs(doc?.[dateKey] || 0);

        if (!baseQty) {
          obj[element.title] = "";
          continue;
        }

        const lines: string[] = [`${baseQty} ${doc?.metricsUnit || ""}`.trim()];
        for (const altUnit of doc?.alternateUnits || []) {
          const convertedValue = Math.abs(
            (altUnit?.conversionfactor || 1) * Number(baseQty)
          ).toFixed(2);
          lines.push(`${convertedValue} ${altUnit?.alternateUnits || ""}`.trim());
        }

        obj[element.title] = lines.join("\n");
        continue;
      }

      obj[element.title] = element.render
        ? element?.render(doc[element.dataIndex], doc)
        : doc[element.dataIndex];
    }
    obj["Download Date"] = formattedDate;
    return obj;
  });
};

const generateItemsData = (
  documents: any[],
  categoriesData: any[],
  itemTableColumns: any,
  selectedRowKeys: any,
  columnPreference: any,
  documentType: any
) => {
  const data: any = [];
  if (documentType === "productionSummary") {
    const expandedDocuments = flattenProductions(documents || []);

    for (const doc of expandedDocuments) {
      const obj: any = {};
      const updatedItemColumns = removeRenderFromColumns(itemTableColumns, ["productionId", "serviceOrderNumber"]);
      for (const element of updatedItemColumns || []) {
        if (
          Array.isArray(columnPreference) &&
          columnPreference.length &&
          !columnPreference.includes(element?.dataIndex)
        ) {
          continue;
        }

        obj[element.title] = element.render
          ? element?.render(doc[element.dataIndex], doc)
          : doc[element.dataIndex];
      }

      data.push(obj);
    }
  }

  else {
    const updatedItemTableColumns = validateDownloadColumns(documentType, itemTableColumns, true);

    for (const doc of documents || []) {
      const supplierState = !AppConstants.PURCHASE_DOCUMENT.includes(
        doc?.documentType,
      )
        ? JSON.parse(doc?.supplierBillingAddress || "{}")?.state
        : JSON.parse(doc?.buyerBillingAddress || "{}")?.state;
      const igstApplicable = supplierState === doc?.supplyState ? false : true;
      for (let item of doc?.items || []) {
        if (Array.isArray(selectedRowKeys) && selectedRowKeys.length) {
          if (!selectedRowKeys.includes(item.id)) continue;
        }
        item = {
          ...item,
          igstApplicable,
          store: doc?.store || item?.store,
          documentNumber: doc.documentNumber,
          documentDate: doc.documentDate,
          buyerName: doc.buyerName,
          deliveryDate: doc.deliveryDate,
          leadCreatedBy: doc.creator?.name,
          quotationCreatedBy: doc.creator?.name,
          orderCreatedBy: doc.creator?.name,
          buyerGSTNumber: doc.buyerGSTNumber,
          leadStatus: AppConstants.DOCUMENT_STATUS[doc.status],
          quotationStatus: AppConstants.DOCUMENT_STATUS[doc.status],

          deliveryCity: doc.deliveryCity,
          deliveryState: doc.deliveryState,
          deliveryCountry: doc.deliveryCountry,
          deliveryPinCode: doc.deliveryPinCode,

          purchaseOrderNumber: doc.purchaseOrderNumber,
          purchaseOrderDate: doc.purchaseOrderDate,

          salesOrderNumber: doc.orderConfirmationNumber,
          salesOrderDate: doc.orderConfirmationDate,
          salesOrderDeliveryDate: doc.deliveryDate,

          challan_number: doc.challan_number,
          purchaseReturnChallanNumber: doc.challan_number,
          purchaseReturnChallanDate: doc.challan_number,
          challan_date: doc.challan_date,
          billDate: doc.billDate,
          paymentDate: doc.paymentDate,
          paymentStatus: doc.paymentStatus,
          paymentTerm: doc.paymentTerm,
          transporterName: doc.transporterName,
          transporterGST: doc.TGNumber,
          salesReturnNumber: doc.salesReturnNumber,
          buyerBillingAddress: doc.buyerBillingAddress,
          customerEmail: doc.buyerEmail,
          customerContactNumber: doc.buyerContactNumber,
          salesPerson: doc.POCName,
          supplyState: doc.supplyState,
          debitNoteNumber: doc.debit_note_number,
          debitNoteDate: doc.debit_note_date,
          creditNoteNumber: doc.creditNoteNumber,
          creditNoteDate: doc.creditNoteDate,
          invoiceNumber: doc.invoiceNumber,
          invoiceDate: doc.invoiceDate,

          indent_number: doc.indent_number,
          indent_date: doc.indent_date,
          rfqNumber: doc.rfqNumber || doc.rfq_number,
          poInvoiceStatus: doc.status,
          deliveryAddress: doc.supplierDeliverAddress,
          receiverGST: doc.creator?.gstNumber,

          grnNumber: doc.documentNumber,
          grnDate: doc.documentDate,

          qualityReportNumber: doc.documentNumber,
          qualityReportDate: doc.documentDate,
          grn_number: doc.grn_number,
          grn_Date: doc.grn_Date,
          supplier_invoice_number: doc.supplier_invoice_number,
          supplier_invoice_date: doc.supplier_invoice_date,
          supplierInvoiceNumber: doc.supplier_invoice_number,
          supplierInvoiceDate: doc.supplier_invoice_date,
          rejectedQuantity: doc.rejectedQuantity,
          rejectedStore: doc.rejectedStore,

          status: AppConstants.DOCUMENT_STATUS[doc.status],
          documentStatus: AppConstants.DOCUMENT_STATUS[doc.status],
          paymentStaus: doc.paymentStaus,

          requestedBy: doc.requestedBy,
          department: doc.department,
          purpose: doc.purpose,
          requiredDate: doc.requiredDate,
          buyerDeliveryAdd: doc.supplierDeliverAddress,
          deliveryGSTIN: doc.creator?.gstNumber,

          purchaseInvoiceNumber: doc.invoiceNumber || doc.supplier_invoice_number,
          purchaseInvoiceDate: doc.invoiceDate || doc.supplier_invoice_date,
          transportationDocumentNumber: doc.TDNumber,
          transportationDocumentDate: doc.TDDate,
          vehicleNumber: doc.VehicleNumber,
        };
        let obj: any = {};
        for (const element of updatedItemTableColumns) {
          if (
            Array.isArray(columnPreference) &&
            columnPreference.length &&
            !columnPreference.includes(element?.dataIndex)
          ) {
            continue;
          }
          obj[element.title] = element.render
            ? element?.render(item[element.dataIndex], item)
            : item[element.dataIndex];
        }
        data.push(obj);
      }
    }
  }
  return data;
};

function normalizeDocumentsData(documentsData: any[], documentType: string) {
  let normalized = [...documentsData]
  if (documentType === "Process Ledger") {
    normalized = documentsData.map((doc) => {
      const processColumns = Object.entries(doc?.processes || {}).reduce(
        (acc, [processName, processData]: [string, any]) => {
          acc[processName + ' (' + processData?.finishedGood + ')'] = processData?.todayProduction ?? processData?.totalProduction ?? "";
          return acc;
        },
        {} as Record<string, any>,
      );

      return {
        ...processColumns,
        date: doc?.date,
        production: doc?.production,
      };
    })
  }
  return normalized;
}

const removeRenderFromColumns = (columns: any, keys: string[]) => {
  return columns.map((col: any) => {

    if (!keys.includes(col?.dataIndex)) {
      return col;
    }

    return {
      ...col,
      render: null,
    };
  });
}

const hyperlinkKeysForDocumentType: Record<string, string[]> = {
  "Inventory Approval report": ["number", "approvalId"],
  "Testing Report": ["productionId"],
  "Testing Report Ledger": ["productionId"],
  "productionSummary": ["bomId", "productionId", "salesOrderNumber"],
  "productionProcess": ["bomId", "productionId", "salesOrderNumber"],
  "gateEntryReport": ["documentNumber"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_REQUEST]: ["documentNumber"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_ORDER]: ["indent_number", "documentNumber"],
  [AppConstants.DOCUMENT_TYPE.GOODS_RECEIVED_NOTE]: ["documentNumber", "purchaseOrderNumber", "supplier_invoice_number", "challan_number"],
  [AppConstants.DOCUMENT_TYPE.QUALITY_REPORT]: ["documentNumber", "grn_number", "purchaseOrderNumber", "supplier_invoice_number"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_INVOICE]: ["documentNumber", "purchaseOrderNumber", "creditNoteNumber", "debit_note_number"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_DEBIT_NOTE]: ["documentNumber", "supplier_invoice_number", "creditNoteNumber"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_CREDIT_NOTE]: ["documentNumber", "debit_note_number", "purchaseOrderNumber", "supplier_invoice_number"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_RETURN]: ["documentNumber", "purchaseOrderNumber", "supplierInvoiceNumber"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_REQUEST + '_split']: ["documentNumber"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_ORDER + '_split']: ["documentNumber", "indent_number"],
  [AppConstants.DOCUMENT_TYPE.GOODS_RECEIVED_NOTE + '_split']: ["documentNumber", "purchaseOrderNumber", "supplier_invoice_number", "challan_number"],
  [AppConstants.DOCUMENT_TYPE.QUALITY_REPORT + '_split']: ["supplier_invoice_number", "qualityReportNumber", "purchaseOrderNumber", "grnNumber"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_INVOICE + '_split']: ["supplier_invoice_number", "purchaseOrderNumber"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_DEBIT_NOTE + '_split']: ["documentNumber", "purchaseInvoiceNumber", "creditNoteNumber"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_CREDIT_NOTE + '_split']: ["documentNumber", "purchaseInvoiceNumber", "debitNoteNumber", "supplier_invoice_number"],
  [AppConstants.DOCUMENT_TYPE.PURCHASE_RETURN + '_split']: ["documentNumber", "purchaseOrderNumber", "supplierInvoiceNumber"],
  [AppConstants.DOCUMENT_TYPE.SALES_LEAD]: ["documentNumber", "quotationNumber"],
  [AppConstants.DOCUMENT_TYPE.SALES_QUOTATION]: ["documentNumber", "enquiryNumber"],
  [AppConstants.DOCUMENT_TYPE.SALES_ORDER]: ["documentNumber", "quotationNumber", "purchaseOrderNumber"],
  [AppConstants.DOCUMENT_TYPE.PROFORMA_INVOICE]: ["documentNumber", "orderConfirmationNumber", "challan_number", "purchaseOrderNumber"],
  [AppConstants.DOCUMENT_TYPE.INVOICE]: ["documentNumber", "orderConfirmationNumber", "challan_number", "purchaseOrderNumber", "creditNoteNumber", "debit_note_number"],
  [AppConstants.DOCUMENT_TYPE.DELIVERY_CHALLAN]: ["documentNumber", "orderConfirmationNumber", "purchaseOrderNumber"],
  [AppConstants.DOCUMENT_TYPE.DEBIT_NOTE]: ["documentNumber", "creditNoteNumber", "invoiceNumber"],
  [AppConstants.DOCUMENT_TYPE.CREDIT_NOTE]: ["documentNumber", "invoiceNumber", "purchaseOrderNumber", "debit_note_number"],
  [AppConstants.DOCUMENT_TYPE.SALES_RETURN]: ["documentNumber", "purchaseOrderNumber", "orderConfirmationNumber", "challan_number", "invoiceNumber"],
  [AppConstants.DOCUMENT_TYPE.SALES_LEAD + '_split']: ["documentNumber"],
  [AppConstants.DOCUMENT_TYPE.SALES_QUOTATION + '_split']: ["documentNumber"],
  [AppConstants.DOCUMENT_TYPE.SALES_ORDER + '_split']: ["documentNumber", "purchaseOrderNumber"],
  [AppConstants.DOCUMENT_TYPE.DELIVERY_CHALLAN + '_split']: ["documentNumber", "salesOrderNumber", "purchaseOrderNumber"],
  [AppConstants.DOCUMENT_TYPE.PROFORMA_INVOICE + '_split']: ["documentNumber", "salesOrderNumber", "purchaseOrderNumber"],
  [AppConstants.DOCUMENT_TYPE.INVOICE + '_split']: ["documentNumber", "salesOrderNumber", "purchaseOrderNumber"],
  [AppConstants.DOCUMENT_TYPE.DEBIT_NOTE + '_split']: ["documentNumber", "creditNoteNumber", "salesOrderNumber", "invoiceNumber"],
  [AppConstants.DOCUMENT_TYPE.CREDIT_NOTE + '_split']: ["documentNumber", "salesOrderNumber", "debitNoteNumber"],
  [AppConstants.DOCUMENT_TYPE.SALES_RETURN + '_split']: ["documentNumber", "salesOrderNumber", "purchaseOrderNumber", "challan_number", "invoiceNumber"],
  "Service Challan Report": ["challanNumber", "grn_number", "serviceOrderNumber", "productionId"],
}

const validateDownloadColumns = (documentType: string, columns: any, split: boolean = false) => {
  const copyColumns = (columns || []).map((col: any) => ({ ...col }));

  const validatedColumns = removeRenderFromColumns(copyColumns, hyperlinkKeysForDocumentType[split ? documentType + '_split' : documentType] || []);

  return validatedColumns
}

function normalizeColumns(columns: any, documentType: string, documentsData: any[]) {
  let normalized = [...columns]
  if (documentType === "Process Ledger") {
    normalized = columns.slice(0, 2);
    const processColumns = columns.slice(2).map((col: any) => ({
      dataIndex: col.dataIndex,
      title: col.dataIndex,
      key: col.dataIndex
    }))
    normalized = [...normalized, ...processColumns]
  }

  if (documentType === "productionReport") {
    const fixedColumns = normalized.slice(0, 4);
    const dynamicDateColumns = normalized.slice(4).map((col: any) => ({
      dataIndex: col.dataIndex,
      title: col.dataIndex,
    }))
    normalized = [...fixedColumns, ...dynamicDateColumns]

  }
  normalized = validateDownloadColumns(documentType, normalized);



  return normalized;
}

export const handleReportDownload = (
  isSplitView: boolean,
  reportType: any,
  documentType: string,
  documentsData: any[],
  selectedRowKeys: any[],
  categoriesData: any[],
  columns: any,
  columnPreference: any,
  itemTableColumns: any,
  applyGroping: boolean,
) => {
  if (!reportType) return;

  const normalizedDocumentsData = normalizeDocumentsData(
    documentsData,
    documentType,
  );
  const normalizedColumns = normalizeColumns(columns, documentType, documentsData);
  const wb = XLSX.utils.book_new();
  if (applyGroping && isSplitView) {
    const columnsKeys = columns?.map((col: any) => col?.dataIndex || '');
    const data: any = [];
    for (const element of normalizedDocumentsData) {
      const obj: any = {};
      for (const temp in element) {
        if (columnsKeys.includes(temp) && (columnPreference?.length == 0 || columnPreference?.includes(temp))) {
          obj[temp?.replace("Group", "")] = element[temp];
        }
      }
      data.push(obj);
    }
    const wsGrouped = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(
      wb,
      wsGrouped,
      `${documentType?.slice(0, 15)} Grouped Report`
    );

    const fileName = `${reportType}_${documentType}_Grouped_Report`;

    XLSX.writeFile(wb, fileName + ".xlsx");

    return;
  }
  let documentsToExport = [];
  if (selectedRowKeys.length === 0 || isSplitView) {
    documentsToExport = normalizedDocumentsData;
  } else if (selectedRowKeys.length === normalizedDocumentsData.length) {
    documentsToExport = normalizedDocumentsData;
  } else {
    documentsToExport = normalizedDocumentsData.filter((doc) =>
      selectedRowKeys.includes(doc.documentNumber),
    );
  }
  // Handle empty data (template case)
  if (documentsToExport.length === 0) {
    return;
  } else {
    if (isSplitView) {
      const itemsData = generateItemsData(
        documentsToExport,
        categoriesData,
        itemTableColumns,
        selectedRowKeys,
        columnPreference,
        documentType
      );
      if (itemsData.length > 0) {
        const wsItems = XLSX.utils.json_to_sheet(itemsData);
        XLSX.utils.book_append_sheet(wb, wsItems, "Items Details");
      }
    } else {
      const mainReportData = generateMainReportData(
        documentType,
        documentsToExport,
        normalizedColumns,
        columnPreference,
      );
      const wsMain = XLSX.utils.json_to_sheet(mainReportData);
      XLSX.utils.book_append_sheet(
        wb,
        wsMain,
        `${documentType?.slice(0, 15)} Report`,
      );
    }
  }

  const fileName =
    documentsToExport.length === 0
      ? `${documentType}_Template`
      : isSplitView
        ? `${reportType}_${documentType}_Items_Report`
        : `${reportType}_${documentType}_Report`;

  XLSX.writeFile(wb, fileName + ".xlsx");
};

export const downloadExcelWithCustomfield = async (
  itemsData: any[],
  name: string,
  uomDataMap: any,
  getCustomFieldsState: any,
  storeOptions?: string[],
  defaultStore?: string,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  const hasStore = Boolean(storeOptions && storeOptions.length > 0);

  // Build dynamic headers
  const baseHeaders = [
    "Item Code",
    "Item Description",
    "Quantity",
    "Unit",
    "Price",
    ...(hasStore ? ["Store"] : []),
  ];
  const customFields = (
    getCustomFieldsState?.data?.data?.filter(
      (data: any) => data.documentType == "item",
    ) || []
  ).map((f: any) => f.fieldName);
  const allHeaders = [...baseHeaders, ...customFields];

  worksheet.addRow(allHeaders); // Header row

  let storeRange = "";
  if (hasStore && storeOptions) {
    const dropdownSheet = workbook.addWorksheet("Dropdowns");
    dropdownSheet.state = "veryHidden";
    const uniqueStores = Array.from(new Set(storeOptions));
    uniqueStores.forEach((val, i) => {
      dropdownSheet.getCell(i + 1, 1).value = val;
    });
    storeRange = `Dropdowns!$A$1:$A$${uniqueStores.length}`;
  }

  const storeColIndex = hasStore ? baseHeaders.indexOf("Store") + 1 : -1;

  // Add rows and prepare dropdowns
  itemsData.forEach((item, rowIndex) => {
    const unitsArray = [
      uomDataMap[item.metricsUnit],
      ...(item?.alternateUnits?.map(
        (au: any) => uomDataMap[au.alternateUnits],
      ) || []),
    ].filter(Boolean);

    const rowData: any[] = [
      item?.itemId,
      item?.itemName,
      0,
      unitsArray[0] || "",
      item?.price || 0,
      ...(hasStore ? [defaultStore || storeOptions?.[0] || ""] : []),
    ];

    const parsedCustomFields = JSON.parse(item.customFields || "{}");
    customFields.forEach((fieldName: any) => {
      rowData.push(parsedCustomFields[fieldName] || "");
    });

    const row = worksheet.addRow(rowData);

    // Add dropdown to "Unit" column (column index = 4)
    const cell = worksheet.getCell(`D${rowIndex + 2}`); // +2 = header + 1-based index
    cell.dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`"${unitsArray.join(",")}"`], // e.g., "Kg,Litre,Box"
    };

    if (hasStore && storeColIndex > 0) {
      const storeCell = row.getCell(storeColIndex);
      storeCell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`=${storeRange}`],
        showErrorMessage: true,
        errorStyle: "stop",
        errorTitle: "Invalid Store",
        error: "Please select a valid Store from the dropdown",
        showInputMessage: true,
        promptTitle: "Store Selection",
        prompt: "Select store",
      };
    }

    // Add dropdown validation for select custom fields
    getCustomFieldsState?.data?.data
      ?.filter((data: any) => data.documentType == "item" && (data.type === "select" || data.type === "dropdown"))
      ?.forEach((cf: any) => {
        let options: string[] = [];
        if (Array.isArray(cf.options)) {
          options = cf.options;
        } else if (typeof cf.options === "string") {
          try {
            const parsed = JSON.parse(cf.options);
            if (Array.isArray(parsed)) options = parsed;
          } catch {
            options = cf.options.split(",").map((s: string) => s.trim()).filter(Boolean);
          }
        }
        if (options.length > 0) {
          const cfColIndex = allHeaders.indexOf(cf.fieldName) + 1;
          if (cfColIndex > 0) {
            const cfCell = row.getCell(cfColIndex);
            cfCell.dataValidation = {
              type: "list",
              allowBlank: true,
              formulae: [`"${options.join(",")}"`],
              showErrorMessage: true,
              errorStyle: "stop",
              errorTitle: `Invalid ${cf.fieldName}`,
              error: `Please select a valid option from the dropdown`,
            };
          }
        }
      });
  });

  // Auto-width
  worksheet.columns.forEach((col: any) => {
    let maxLength = 10;
    col.eachCell({ includeEmpty: true }, (cell: any) => {
      const val = cell.value?.toString() || "";
      maxLength = Math.max(maxLength, val.length);
    });
    col.width = maxLength + 2;
  });

  // Export
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${name}.xlsx`);
};

export const downloadExcelWithUOMDropdown = async (
  data: any[],
  fileName: string,
  uomOptions: string[],
  storeOptions?: string[],
  customFieldsState?: any,
) => {
  if (!data.length) throw new Error("Data cannot be empty");

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Items");

  // Ensure all rows have 'Unit' column
  data.forEach((row) => {
    if (!row.hasOwnProperty("Unit")) {
      row["Unit"] = "";
    }
  });

  // Set worksheet columns
  const headers = Object.keys(data[0]);
  worksheet.columns = headers.map((h) => ({
    header: h,
    key: h,
    width: 20,
  }));

  // Add data rows
  data.forEach((row) => worksheet.addRow(row));

  // Create hidden Dropdowns sheet
  const dropdownSheet = workbook.addWorksheet("Dropdowns");
  dropdownSheet.state = "veryHidden";

  // Fill UOM options in column A of Dropdowns sheet
  if (uomOptions && uomOptions.length > 0) {
    uomOptions.forEach((val, i) => {
      dropdownSheet.getCell(i + 1, 1).value = val;
    });

    const dropdownRange = `Dropdowns!$A$1:$A$${uomOptions.length}`;
    const unitColIndex = headers.indexOf("Unit") + 1;
    if (unitColIndex > 0) {
      for (let rowNum = 2; rowNum <= Math.max(data.length + 1, 500); rowNum++) {
        worksheet.getRow(rowNum).getCell(unitColIndex).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`=${dropdownRange}`],
          showErrorMessage: true,
          errorStyle: "stop",
          errorTitle: "Invalid Unit",
          error: "Please select a valid Unit from the dropdown",
          showInputMessage: true,
          promptTitle: "Unit Selection",
          prompt: "Select unit of measure",
        };
      }
    }
  }

  // Fill Store options in column B of Dropdowns sheet if provided
  if (storeOptions && storeOptions.length > 0) {
    const uniqueStores = Array.from(new Set(storeOptions));
    uniqueStores.forEach((val, i) => {
      dropdownSheet.getCell(i + 1, 2).value = val;
    });

    const storeDropdownRange = `Dropdowns!$B$1:$B$${uniqueStores.length}`;
    const storeColIndex = headers.indexOf("Store") + 1;
    if (storeColIndex > 0) {
      for (let rowNum = 2; rowNum <= Math.max(data.length + 1, 500); rowNum++) {
        worksheet.getRow(rowNum).getCell(storeColIndex).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`=${storeDropdownRange}`],
          showErrorMessage: true,
          errorStyle: "stop",
          errorTitle: "Invalid Store",
          error: "Please select a valid Store from the dropdown",
          showInputMessage: true,
          promptTitle: "Store Selection",
          prompt: "Select store",
        };
      }
    }
  }

  // Add dropdown validation for select custom fields if provided
  if (customFieldsState?.data?.data) {
    customFieldsState.data.data
      ?.filter((cf: any) => cf.type === "select" || cf.type === "dropdown")
      ?.forEach((cf: any) => {
        let options: string[] = [];
        if (Array.isArray(cf.options)) {
          options = cf.options;
        } else if (typeof cf.options === "string") {
          try {
            const parsed = JSON.parse(cf.options);
            if (Array.isArray(parsed)) options = parsed;
          } catch {
            options = cf.options.split(",").map((s: string) => s.trim()).filter(Boolean);
          }
        }
        if (options.length > 0) {
          const cfColIndex = headers.indexOf(cf.fieldName) + 1;
          if (cfColIndex > 0) {
            for (let rowNum = 2; rowNum <= Math.max(data.length + 1, 500); rowNum++) {
              worksheet.getRow(rowNum).getCell(cfColIndex).dataValidation = {
                type: "list",
                allowBlank: true,
                formulae: [`"${options.join(",")}"`],
                showErrorMessage: true,
                errorStyle: "stop",
                errorTitle: `Invalid ${cf.fieldName}`,
                error: `Please select a valid option from the dropdown`,
              };
            }
          }
        }
      });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
};

export const downloadExcelWithBuyerSupplierDropdowns = async (
  fileName: string,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("BuyerSupplier");

  const companyTypeOptions = ["Buyer", "Supplier", "Both"];
  const addressTypeOptions = ["Delivery Address", "Billing Address", "Both"];
  const stateOptions = [
    "Andaman and Nicobar Islands",
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chandigarh",
    "Chhattisgarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Ladakh",
    "Lakshadweep",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Puducherry",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];
  const gstTypeOptions = ["Regular", "Composition", "Unregistered"];

  const requiredFields = [
    "* Person Name",
    "Person Email",
    "* Phone",
    "* Company Name",
    "Company Email",
    "* Company Type",
    "GST Number",
    "GST Type",
    "PAN Number",
    "* Address",
    "* Address Type",
    "Pin Code",
    "* City",
    "* State",
  ];

  worksheet.columns = requiredFields.map((field) => ({
    header: field,
    key: field,
    width: 25,
  }));

  const dummyRow = {
    "* Person Name": "Contact Person Name",
    "Person Email": "Contact Person Email",
    "* Phone": "+917777777777",
    "* Company Name": "",
    "Company Email": "",
    "* Company Type": "Both",
    "GST Number": "",
    "GST Type": "",
    "* Address": "",
    "* Address Type": "Both",
    "Pin Code": "",
    "* City": "",
    "* State": "",
  };
  worksheet.addRow(dummyRow);

  const dropdownSheet = workbook.addWorksheet("Dropdowns");
  dropdownSheet.state = "veryHidden";

  // Fill dropdown options into Dropdowns sheet
  companyTypeOptions.forEach((val, i) => {
    dropdownSheet.getCell(i + 1, 1).value = val; // Column A
  });
  addressTypeOptions.forEach((val, i) => {
    dropdownSheet.getCell(i + 1, 2).value = val; // Column B
  });
  stateOptions.forEach((val, i) => {
    dropdownSheet.getCell(i + 1, 3).value = val; // Column C
  });
  gstTypeOptions.forEach((val, i) => {
    dropdownSheet.getCell(i + 1, 4).value = val; // Column D
  });

  const companyTypeRange = `Dropdowns!$A$1:$A$${companyTypeOptions.length}`;
  const addressTypeRange = `Dropdowns!$B$1:$B$${addressTypeOptions.length}`;
  const stateRange = `Dropdowns!$C$1:$C$${stateOptions.length}`;
  const gstTypeRange = `Dropdowns!$D$1:$D$${gstTypeOptions.length}`;

  // Get column indexes (1-based) of dropdown fields
  const companyTypeColIndex = requiredFields.indexOf("* Company Type") + 1;
  const addressTypeColIndex = requiredFields.indexOf("* Address Type") + 1;
  const stateColIndex = requiredFields.indexOf("* State") + 1;
  const gstTypeColIndex = requiredFields.indexOf("GST Type") + 1;

  // Apply dropdowns from row 2 to 1001
  for (let row = 2; row <= 1001; row++) {
    worksheet.getCell(row, companyTypeColIndex).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${companyTypeRange}`],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid Company Type",
      error: "Select a valid Company Type",
      showInputMessage: true,
      promptTitle: "Company Type",
      prompt: "Choose Buyer, Supplier or Both",
    };

    worksheet.getCell(row, addressTypeColIndex).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${addressTypeRange}`],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid Address Type",
      error: "Select a valid Address Type",
      showInputMessage: true,
      promptTitle: "Address Type",
      prompt: "Choose Delivery, Billing or Both",
    };

    worksheet.getCell(row, stateColIndex).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${stateRange}`],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid State",
      error: "Select a valid State",
      showInputMessage: true,
      promptTitle: "State",
      prompt: "Choose a valid Indian state",
    };

    worksheet.getCell(row, gstTypeColIndex).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${gstTypeRange}`],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid GST Type",
      error: "Select either Regular or Non Regular",
      showInputMessage: true,
      promptTitle: "GST Type",
      prompt: "Choose Regular or Non Regular",
    };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
};

export const downloadBuyerSupplierExcelErrorData = async (
  data: any[],
  fileName: string,
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("BuyerSupplier");

  const companyTypeOptions = ["Buyer", "Supplier", "Both"];
  const addressTypeOptions = ["Delivery Address", "Billing Address", "Both"];
  const stateOptions = [
    "Andaman and Nicobar Islands",
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chandigarh",
    "Chhattisgarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jammu and Kashmir",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Ladakh",
    "Lakshadweep",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Puducherry",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
  ];

  const gstTypeOptions = ["Regular", "Composition", "Unregistered"];

  const baseFields = [
    "* Person Name",
    "Person Email",
    "* Phone",
    "* Company Name",
    "Company Email",
    "* Company Type",
    "GST Number",
    "GST Type",
    "PAN Number",
    "* Address",
    "* Address Type",
    "Pin Code",
    "* City",
    "* State",
  ];

  const hasErrors = data.some((row) => row.Error);
  const fields = hasErrors ? [...baseFields, "Error"] : baseFields;

  // Set columns
  worksheet.columns = fields.map((field) => ({
    header: field,
    key: field,
    width: 25,
  }));

  // Add all data rows
  data.forEach((row) => {
    const formattedRow: any = {};
    fields.forEach((field) => {
      formattedRow[field] = row[field] || "";
    });
    worksheet.addRow(formattedRow);
  });

  // Add Dropdowns Sheet
  const dropdownSheet = workbook.addWorksheet("Dropdowns");
  dropdownSheet.state = "veryHidden";

  companyTypeOptions.forEach((val, i) => {
    dropdownSheet.getCell(i + 1, 1).value = val; // Column A
  });
  addressTypeOptions.forEach((val, i) => {
    dropdownSheet.getCell(i + 1, 2).value = val; // Column B
  });
  stateOptions.forEach((val, i) => {
    dropdownSheet.getCell(i + 1, 3).value = val; // Column C
  });
  gstTypeOptions.forEach((val, i) => {
    dropdownSheet.getCell(i + 1, 4).value = val; // Column D
  });

  const companyTypeRange = `Dropdowns!$A$1:$A$${companyTypeOptions.length}`;
  const addressTypeRange = `Dropdowns!$B$1:$B$${addressTypeOptions.length}`;
  const stateRange = `Dropdowns!$C$1:$C$${stateOptions.length}`;
  const gstTypeRange = `Dropdowns!$D$1:$D$${gstTypeOptions.length}`;

  const companyTypeColIndex = fields.indexOf("* Company Type") + 1;
  const addressTypeColIndex = fields.indexOf("* Address Type") + 1;
  const stateColIndex = fields.indexOf("* State") + 1;
  const gstTypeColIndex = fields.indexOf("GST Type") + 1;

  // Apply dropdowns to all applicable rows
  for (let i = 0; i < data.length; i++) {
    const rowNum = i + 2; // +1 for header row

    worksheet.getRow(rowNum).getCell(companyTypeColIndex).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${companyTypeRange}`],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid Company Type",
      error: "Select a valid Company Type",
    };

    worksheet.getRow(rowNum).getCell(addressTypeColIndex).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${addressTypeRange}`],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid Address Type",
      error: "Select a valid Address Type",
    };

    worksheet.getRow(rowNum).getCell(stateColIndex).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${stateRange}`],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid State",
      error: "Select a valid State",
    };

    worksheet.getRow(rowNum).getCell(gstTypeColIndex).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${gstTypeRange}`],
      showErrorMessage: true,
      errorStyle: "stop",
      errorTitle: "Invalid GST Type",
      error: "Select either Regular or Non Regular",
    };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  saveAs(blob, `${fileName}.xlsx`);
};

export const downloadExcelTemplateForBulkStockUpdate = async (
  items: { itemName: string; itemId: string }[],
  stores: string[],
  withItems: boolean,
  errorData?: any,
) => {
  const sortedItems = [...items].sort((a, b) =>
    a.itemName.localeCompare(b.itemName),
  );

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Template");
  const hiddenSheet = workbook.addWorksheet("DropdownData");
  hiddenSheet.state = "veryHidden";

  // Populate hidden sheet only for store dropdown
  stores.forEach((s, idx) => {
    hiddenSheet.getCell(`A${idx + 1}`).value = s;
  });

  // Define columns
  worksheet.columns = [
    { header: withItems ? "Item" : "Item Name/Id", key: "item", width: 30 },
    { header: "Store", key: "store", width: 25 },
    { header: "Quantity", key: "quantity", width: 15 },
    { header: "Price", key: "price", width: 15 },
    { header: "Type", key: "type", width: 15 },
    { header: "Comment", key: "comment", width: 30 },
    ...(errorData ? [{ header: "Error", key: "error", width: 40 }] : []),
  ];

  if (errorData) {
    errorData.forEach((row: any) => {
      worksheet.addRow({
        item: row["Item"] || row["Item Name/Id"],
        store: row.Store || "",
        quantity: row.Quantity || "",
        price: row.Price || "",
        type: row.Type || "Add",
        comment: row.Comment || "",
        error: row.Error || "",
      });
    });
  } else if (withItems) {
    // 🔹 Add a row for every item
    sortedItems.forEach((i) => {
      worksheet.addRow({
        item: `${i.itemName} (${i.itemId})`,
        store: "",
        quantity: "",
        price: "",
        type: "Add",
        comment: "",
      });
    });
  } else {
    // 🔹 Add 500 dummy rows (all empty, user fills them)
    for (let i = 0; i < 500; i++) {
      worksheet.addRow({
        item: "",
        store: "",
        quantity: "",
        price: "",
        type: "",
        comment: "",
      });
    }
  }

  // Store dropdown
  worksheet.getColumn("store").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [`DropdownData!$A$1:$A$${stores.length}`],
      };
    }
  });

  // Quantity validation
  worksheet.getColumn("quantity").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "decimal",
        operator: "greaterThanOrEqual",
        formulae: [0],
        allowBlank: true,
        showErrorMessage: true,
        errorTitle: "Invalid input",
        error: "Please enter a valid number (whole or decimal)",
      };
    }
  });

  // Price validation
  worksheet.getColumn("price").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "decimal",
        operator: "greaterThanOrEqual",
        formulae: [0],
        allowBlank: true,
        showErrorMessage: true,
        errorTitle: "Invalid Price",
        error: "Please enter a valid price (whole or decimal)",
      };
    }
  });

  // Type dropdown
  worksheet.getColumn("type").eachCell((cell, rowNumber) => {
    if (rowNumber > 1) {
      cell.dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"Add,Reduce"'],
      };
    }
  });

  // Save file
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer]),
    `${errorData ? "StockUpdateError" : "StockUpdate"}.xlsx`,
  );
};
